import { useMemo } from 'react';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useSiadap } from '@/context/SiadapContext';
import type { Profile } from '@/context/AuthContext';

interface AdminStatsBarProps {
  users: Profile[];
  loadingUsers: boolean;
}

export default function AdminStatsBar({ users, loadingUsers }: AdminStatsBarProps) {
  const { funcionarios, loading: loadingFunc } = useFuncionarios();
  const { registosSiadap, registosHistorico, loadingSiadap } = useSiadap();

  const isLoading = loadingFunc || loadingSiadap || loadingUsers;

  const stats = useMemo(() => {
    const totalFunc = funcionarios.length;
    const totalSiadap = registosSiadap.length;
    const totalHistorico = registosHistorico.length;
    const totalUsers = users.length;

    const carreiras = new Set(funcionarios.map((f) => f.carreira)).size;

    const avalDist: Record<string, number> = {};
    registosSiadap.forEach((r) => {
      avalDist[r.avaliacao] = (avalDist[r.avaliacao] ?? 0) + 1;
    });
    const topAval = Object.entries(avalDist).sort((a, b) => b[1] - a[1])[0];

    return { totalFunc, totalSiadap, totalHistorico, totalUsers, carreiras, topAval };
  }, [funcionarios, registosSiadap, registosHistorico, users]);

  const cards = [
    {
      label: 'Funcionários',
      value: isLoading ? '—' : String(stats.totalFunc),
      sub: `${stats.carreiras} carreira${stats.carreiras !== 1 ? 's' : ''}`,
      icon: 'ri-group-line',
      iconBg: 'bg-zinc-100',
      iconColor: 'text-zinc-600',
    },
    {
      label: 'Avaliações SIADAP',
      value: isLoading ? '—' : String(stats.totalSiadap),
      sub: stats.topAval ? `Mais freq.: ${stats.topAval[0]}` : 'Sem registos',
      icon: 'ri-award-line',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    {
      label: 'Histórico Manual',
      value: isLoading ? '—' : String(stats.totalHistorico),
      sub: 'Registos anteriores a 2023',
      icon: 'ri-history-line',
      iconBg: 'bg-amber-50',
      iconColor: 'text-amber-600',
    },
    {
      label: 'Utilizadores',
      value: isLoading ? '—' : String(stats.totalUsers),
      sub: `${users.filter((u) => u.role === 'super_admin' || u.role === 'admin').length} admin${users.filter((u) => u.role === 'super_admin' || u.role === 'admin').length !== 1 ? 's' : ''}`,
      icon: 'ri-shield-user-line',
      iconBg: 'bg-zinc-100',
      iconColor: 'text-zinc-500',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
      {cards.map((c) => (
        <div key={c.label} className="bg-white rounded-xl border border-zinc-100 px-5 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest leading-tight">{c.label}</span>
            <div className={`w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0 ${c.iconBg}`}>
              <i className={`${c.icon} text-sm ${c.iconColor}`} />
            </div>
          </div>
          {isLoading ? (
            <div className="h-7 w-12 bg-zinc-100 rounded animate-pulse mb-1" />
          ) : (
            <p className="text-[22px] font-bold text-zinc-900 leading-none">{c.value}</p>
          )}
          <p className="text-[11px] text-zinc-400 mt-1.5">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}
